import { analyserMontant, formaterMontant } from '@admin-pmvb/core'
import { type FormEvent, useId, useState } from 'react'
import { ChoixCherchable } from './ChoixCherchable'
import { ErreurApi } from '../api'

// Formulaire générique : décrit par une liste de champs, il convertit les saisies
// (montants en euros vers centimes, champ vide vers null) et affiche les erreurs de l'API.

type Base = { nom: string; libelle: string; requis?: boolean; aide?: string }
export type Champ =
  | (Base & { type: 'texte' | 'email' | 'date' | 'heure' })
  | (Base & { type: 'zone'; lignes?: number })
  | (Base & {
      type: 'choix'
      options: { valeur: string; libelle: string }[]
      /** Longue liste : on tape pour chercher au lieu de faire défiler. */
      cherchable?: boolean
    })
  | (Base & { type: 'booleen' })
  | (Base & { type: 'montant' })
  | (Base & { type: 'entier' })
  | (Base & { type: 'liste' })

type Valeurs = Record<string, unknown>

function versSaisie(champ: Champ, valeur: unknown): string | boolean {
  if (champ.type === 'booleen') return valeur === true
  if (valeur === null || valeur === undefined) return ''
  if (champ.type === 'montant') return formaterMontant(valeur as number).replace(/\s*€$/, '')
  if (champ.type === 'liste') return (valeur as string[]).join('\n')
  return String(valeur)
}

function depuisSaisie(
  champ: Champ,
  saisie: string | boolean,
): { valeur?: unknown; erreur?: string } {
  if (champ.type === 'booleen') return { valeur: saisie === true }
  const texte = String(saisie).trim()
  if (texte === '' && champ.type === 'liste' && !champ.requis) return { valeur: [] }
  if (texte === '') return champ.requis ? { erreur: 'Obligatoire.' } : { valeur: null }
  if (champ.type === 'montant') {
    const centimes = analyserMontant(texte)
    return centimes === null
      ? { erreur: 'Montant invalide (exemple : 1 234,56).' }
      : { valeur: centimes }
  }
  if (champ.type === 'liste') {
    return {
      valeur: texte
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean),
    }
  }
  if (champ.type === 'heure') {
    return /^([01]\d|2[0-3]):[0-5]\d$/.test(texte)
      ? { valeur: texte }
      : { erreur: 'Heure attendue (exemple : 09:30).' }
  }
  if (champ.type === 'entier') {
    return /^-?\d+$/.test(texte) ? { valeur: Number(texte) } : { erreur: 'Nombre entier attendu.' }
  }
  return { valeur: texte }
}

export function Formulaire({
  champs,
  initial = {},
  libelleBouton = 'Enregistrer',
  onValider,
  onAnnuler,
  enLigne = false,
  surSaisie,
}: {
  champs: Champ[]
  initial?: Valeurs
  libelleBouton?: string
  onValider: (valeurs: Valeurs) => Promise<unknown>
  onAnnuler?: () => void
  /** Saisie sur place dans une fiche : compacte, premier champ actif, Échap annule. */
  enLigne?: boolean
  /** Ce qui est saisi, à chaque changement : ce que le formulaire ne valide pas encore. */
  surSaisie?: (saisies: Record<string, string | boolean>) => void
}) {
  const prefixe = useId()
  const [saisies, setSaisies] = useState<Record<string, string | boolean>>(() =>
    Object.fromEntries(champs.map((c) => [c.nom, versSaisie(c, initial[c.nom])])),
  )
  const [erreurs, setErreurs] = useState<Record<string, string>>({})
  const [erreurGenerale, setErreurGenerale] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)

  async function soumettre(e: FormEvent) {
    e.preventDefault()
    const valeurs: Valeurs = {}
    const locales: Record<string, string> = {}
    for (const champ of champs) {
      const r = depuisSaisie(champ, saisies[champ.nom] ?? '')
      if (r.erreur) locales[champ.nom] = r.erreur
      else valeurs[champ.nom] = r.valeur
    }
    setErreurs(locales)
    setErreurGenerale(null)
    if (Object.keys(locales).length > 0) return
    setEnCours(true)
    try {
      await onValider(valeurs)
    } catch (err) {
      if (err instanceof ErreurApi) {
        setErreurGenerale(err.titre)
        // « objets.0 » : l'erreur d'une ligne de liste s'affiche sous la liste.
        setErreurs(Object.fromEntries(err.champs.map((c) => [c.champ.split('.')[0]!, c.message])))
      } else {
        setErreurGenerale('Erreur inattendue.')
      }
    } finally {
      setEnCours(false)
    }
  }

  return (
    <form
      className={enLigne ? 'formulaire formulaire-en-ligne' : 'formulaire'}
      onSubmit={soumettre}
      onKeyDown={
        enLigne && onAnnuler
          ? (e) => {
              if (e.key === 'Escape') onAnnuler()
            }
          : undefined
      }
      noValidate
    >
      {champs.map((champ, rang) => {
        const id = `${prefixe}-${champ.nom}`
        const actif = enLigne && rang === 0
        const valeur = saisies[champ.nom]
        const maj = (v: string | boolean) => {
          setSaisies((s) => ({ ...s, [champ.nom]: v }))
          surSaisie?.({ ...saisies, [champ.nom]: v })
        }
        return (
          <div key={champ.nom} className={champ.type === 'booleen' ? 'champ champ-case' : 'champ'}>
            {champ.type === 'booleen' ? (
              <>
                <input
                  id={id}
                  type="checkbox"
                  autoFocus={actif}
                  checked={valeur === true}
                  onChange={(e) => maj(e.target.checked)}
                />
                <label htmlFor={id}>{champ.libelle}</label>
              </>
            ) : (
              <>
                <label htmlFor={id}>
                  {champ.libelle}
                  {champ.requis && <span aria-hidden="true"> *</span>}
                </label>
                {champ.type === 'choix' && champ.cherchable ? (
                  <ChoixCherchable
                    id={id}
                    autoFocus={actif}
                    options={champ.options}
                    valeur={String(valeur)}
                    onChange={maj}
                  />
                ) : champ.type === 'choix' ? (
                  <select
                    id={id}
                    autoFocus={actif}
                    value={String(valeur)}
                    onChange={(e) => maj(e.target.value)}
                  >
                    <option value="">{champ.requis ? 'Choisir…' : 'Aucun'}</option>
                    {champ.options.map((o) => (
                      <option key={o.valeur} value={o.valeur}>
                        {o.libelle}
                      </option>
                    ))}
                  </select>
                ) : champ.type === 'liste' || champ.type === 'zone' ? (
                  <textarea
                    id={id}
                    autoFocus={actif}
                    rows={champ.type === 'zone' ? (champ.lignes ?? 8) : 3}
                    value={String(valeur)}
                    onChange={(e) => maj(e.target.value)}
                  />
                ) : (
                  <input
                    id={id}
                    autoFocus={actif}
                    type={
                      champ.type === 'email'
                        ? 'email'
                        : champ.type === 'date'
                          ? 'date'
                          : champ.type === 'heure'
                            ? 'time'
                            : 'text'
                    }
                    inputMode={
                      champ.type === 'montant'
                        ? 'decimal'
                        : champ.type === 'entier'
                          ? 'numeric'
                          : undefined
                    }
                    value={String(valeur)}
                    onChange={(e) => maj(e.target.value)}
                  />
                )}
              </>
            )}
            {champ.aide && <p className="aide">{champ.aide}</p>}
            {erreurs[champ.nom] && <p className="alerte">{erreurs[champ.nom]}</p>}
          </div>
        )
      })}
      {erreurGenerale && <p className="alerte">{erreurGenerale}</p>}
      <div className="boutons">
        <button type="submit" className="bouton" disabled={enCours}>
          {enCours ? 'Enregistrement…' : libelleBouton}
        </button>
        {onAnnuler && (
          <button type="button" className="bouton-discret" onClick={onAnnuler}>
            Annuler
          </button>
        )}
      </div>
    </form>
  )
}
